import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, Calendar } from "@untitledui/icons";
import { SubmissionFooter } from "../../../components/submission-footer";
import { useSubmissionContext } from "../context";
import { getNextSection } from "../sections";
import { saveCreate, saveEdit } from "./save";
import { type UwInformationFormData, uwInformationSchema } from "./schema";
import { AccountBasicsSection } from "./sections/account-basics";
import { AdditionalInsuredSection } from "./sections/additional-insured";
import { BrokerageSection } from "./sections/brokerage";
import { GeminiContactsSection } from "./sections/gemini-contacts";
import { GeminiOnboardingContactsSection } from "./sections/gemini-onboarding-contacts";
import { NamedInsuredsSection } from "./sections/named-insureds";
// TODO(linda-2026-05-19): Schedule of Location table removed from UW
// Information per Linda's confirmation; SoT for facilities remains in
// the Schedule section. Restore this import + the JSX usage below
// when the table needs to come back.
// import { ScheduleOfLocationSection } from "./sections/schedule-of-location";
import { SubmissionNotesSection } from "./sections/submission-notes";

const SECTION_ID = "uw-information";

function detailToFormData(detail: ReturnType<typeof useSubmissionContext>["detail"]): UwInformationFormData {
    return {
        accountBasics: detail.accountBasics,
        brokerage: detail.brokerage,
        geminiContacts: detail.geminiContacts,
        note: detail.note ?? "",
        namedInsureds: detail.namedInsureds,
        additionalInsureds: detail.additionalInsureds,
    };
}

export function UwInformationPage() {
    const { detail, setDetail, mode } = useSubmissionContext();
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const methods = useForm<UwInformationFormData>({
        resolver: zodResolver(uwInformationSchema),
        defaultValues: detailToFormData(detail),
        // onTouched: validate the first time a field loses focus, then
        // re-validate on every change after that. Keeps the inline
        // hint message tracking what the user is typing, so an
        // invalid date (e.g. 02/40/2025) shows the error the moment
        // they correct or blur the field.
        mode: "onTouched",
    });

    /** Save and return the canonical submission id on success, or
     *  null on error. The caller decides whether to navigate. */
    const persist = useCallback(
        async (data: UwInformationFormData): Promise<string | null> => {
            setIsSaving(true);
            setSaveError(null);
            try {
                const refreshed = mode === "create" ? await saveCreate(detail, data) : await saveEdit(detail.id, detail, data);
                setDetail(refreshed);
                methods.reset(detailToFormData(refreshed));
                return refreshed.id;
            } catch (error) {
                console.error("Failed to save UW information", error);
                setSaveError(error instanceof Error ? error.message : "Save failed — check the network tab.");
                return null;
            } finally {
                setIsSaving(false);
            }
        },
        [detail, methods, mode, setDetail],
    );

    // Save validates + persists without navigating; Continue advances (the
    // footer guards against leaving with unsaved changes).
    // Field-level errors render inline; we just scroll the first invalid
    // control into view (no aggregated banner anymore).
    const scrollToFirstInvalid = () => {
        requestAnimationFrame(() => {
            const first = document.querySelector<HTMLElement>('[aria-invalid="true"]');
            if (!first) return;
            first.scrollIntoView({ behavior: "smooth", block: "center" });
            if (typeof first.focus === "function") first.focus({ preventScroll: true });
        });
    };

    const onSave = methods.handleSubmit(async (data) => {
        const id = await persist(data);
        // In create mode, persist() just minted a brand-new submission; jump
        // from /submissions/new to its edit URL so Save lands on the saved
        // record. In edit mode we're already there, so stay put.
        if (id && (mode === "create" || !detail.id)) {
            navigate(`/submissions/${id}/uw-information`);
        }
    }, scrollToFirstInvalid);

    const next = getNextSection(SECTION_ID);
    const goToNext = (id: string) => navigate(next ? `/submissions/${id}/${next.path}` : "/submissions");

    // In create mode there's no submission yet, so Continue can't just
    // navigate — the next section needs a real id and the required fields
    // filled. Validate + create first (surfacing required-field errors),
    // and only advance once the submission exists. In edit mode the
    // submission is already there, so navigate straight through (the
    // footer's dirty guard handles unsaved edits).
    const onContinue =
        mode === "create" || !detail.id
            ? methods.handleSubmit(async (data) => {
                  const id = await persist(data);
                  if (id) goToNext(id);
              }, scrollToFirstInvalid)
            : () => goToNext(detail.id);
    const isDirty = methods.formState.isDirty;

    return (
        <FormProvider {...methods}>
            <HeaderSync />
            <div className="w-full max-w-dashboard space-y-6">
                <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-primary text-md font-semibold">UW Information</h2>
                        <p className="text-tertiary text-sm">
                            General underwriting details, contacts, and coverage specifics
                        </p>
                    </div>
                    <CoverageDates control={methods.control} />
                </header>

                <AccountBasicsSection />
                <BrokerageSection />
                {/* Gemini Onboarding Contacts sits ABOVE Facility Contacts —
                  * users asked for the reorder (design, OR-1310). */}
                <GeminiOnboardingContactsSection />
                <GeminiContactsSection />
                <SubmissionNotesSection />
                <NamedInsuredsSection />
                <AdditionalInsuredSection />
                {/* TODO(linda-2026-05-19): Schedule of Location removed
                  * from UW Information; facilities SoT lives in the
                  * Schedule section now. Uncomment the import above
                  * and this line to bring it back. */}
                {/* <ScheduleOfLocationSection /> */}

                {saveError && (
                    <div
                        role="alert"
                        className="border-error-subtle bg-error-primary text-error-primary flex gap-3 rounded-xl border p-4 text-sm"
                    >
                        <AlertTriangle aria-hidden="true" className="size-5 shrink-0" />
                        <span>{saveError}</span>
                    </div>
                )}
            </div>

            <SubmissionFooter
                onSave={onSave}
                onContinue={onContinue}
                isDirty={isDirty}
                isSaving={isSaving}
                nextSectionLabel={next?.label ?? null}
            />
        </FormProvider>
    );
}

// ─── Sync live form values into the parent SubmissionContext so the
//     SubmissionHeader subtitle (brokerage · type · FNI) updates as the
//     user fills the form, even before the first save. ─────────────────

function HeaderSync() {
    const { detail, setDetail } = useSubmissionContext();
    const { control } = useFormContext<UwInformationFormData>();
    const brokerageName = useWatch({ control, name: "brokerage.brokerageName" }) ?? "";
    const type = useWatch({ control, name: "accountBasics.type" }) ?? "";
    const fni = useWatch({ control, name: "accountBasics.fni" }) ?? "";

    useEffect(() => {
        const sameBrokerage = (detail.brokerage.brokerageName ?? "") === brokerageName;
        const sameType = (detail.accountBasics.type ?? "") === type;
        const sameFni = (detail.accountBasics.fni ?? "") === fni;
        if (sameBrokerage && sameType && sameFni) return;
        setDetail({
            ...detail,
            accountBasics: { ...detail.accountBasics, type: type as typeof detail.accountBasics.type, fni },
            brokerage: { ...detail.brokerage, brokerageName },
        });
    }, [brokerageName, type, fni, detail, setDetail]);

    return null;
}

// ─── Header date strip ───────────────────────────────────────────────────────

function CoverageDates({ control }: { control: ReturnType<typeof useForm<UwInformationFormData>>["control"] }) {
    const renewal = useWatch({ control, name: "accountBasics.renewalDate" }) || "";
    const expiration = useWatch({ control, name: "accountBasics.expirationDate" }) || "";

    return (
        // Mobile: dates spread to fill the row (50/50, no divider — the
        // gap reads as separation enough on small viewports). md+: collapse
        // back to content-width with the divider visible, sitting tightly
        // to the right of the page title.
        <div className="flex w-full items-stretch justify-between gap-6 md:w-auto md:shrink-0 md:justify-start">
            <DateField label="Renewal Date" value={renewal} />
            <span aria-hidden="true" className="bg-border-secondary hidden w-px self-stretch md:block" />
            <DateField label="Expiration Date" value={expiration} />
        </div>
    );
}

function DateField({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-tertiary text-xs font-normal">{label}</span>
            <span className="text-secondary inline-flex items-center gap-1.5 text-xs font-semibold">
                <Calendar aria-hidden="true" className="text-fg-quaternary size-4" />
                {value || <span className="text-placeholder font-normal">—</span>}
            </span>
        </div>
    );
}
